import Draggable from 'vuedraggable';
import { listComponentMixin } from "../../../../lib/helpers";
import { memoize } from "lodash-es";
const __script_base__ = {
  name: 'WArray',
  mixins: [listComponentMixin],
  props: {
    layoutMode: {
      type: String,
      default: 'fixedColumnCount'
    },
    gap: {
      type: String,
      default: ''
    },
    columnCount: {
      type: Number,
      default: 1
    },
    columnWidth: {
      type: String,
      default: '300px'
    },
    autoColumnCountStrategy: {
      type: String,
      default: 'grow'
    },
    selectedIndex: {
      type: [Array, Number],
      default: () => []
    },
    isSingleSelect: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Boolean,
      default: true
    },
    addButton: {
      type: String,
      default: 'default'
    },
    dragOrder: {
      type: String,
      default: 'none'
    },
    dragAction: {
      type: String,
      default: 'mutateArray'
    },
    deleteButton: {
      type: String,
      default: 'none'
    },
    onDragEnd: {
      type: Function,
      default: null
    },
    onSelect: {
      type: Function,
      default: null
    },
    enableHeaderSlot: {
      type: Boolean,
      default: false
    },
    enableFooterSlot: {
      type: Boolean,
      default: false
    },
    /** 开启后 对于空的作用域插槽 隐藏他的渲染 */
    hiddenItemWhenEmpty: {
      type: Boolean,
      default: false
    },
    addItemButtonText: {
      type: String,
      default: ''
    },
    handleAdd: {
      type: Function,
      default: null
    }
  },
  components: {
    Draggable
  },
  data() {
    return {
      dragging: false,
      emulateEmptyMode: false,
      /** 用来决定是否要渲染[index]这个位置上的内容 */
      isItemHidden: []
    };
  },
  created() {
    this.$watch(() => +this.arrayLength || 0, len => {
      if (this.isItemHidden.length > len) this.isItemHidden.splice(len);
    });
  },
  computed: {
    hasCustomHeaderOrFooter() {
      return !!(this.enableHeaderSlot || this.enableFooterSlot);
    },
    canDragItem() {
      if (this.finalReadonly) return false;
      return this.dragOrder !== 'none';
    },
    canDeleteItem() {
      if (this.finalReadonly) return false;
      return this.deleteButton !== 'none';
    },
    actualLayoutMode() {
      // 历史遗留原因，columnCount == -1 的时候为混排
      if (this.layoutMode === 'fixedColumnCount' && this.columnCount <= 0) return 'inlineBlocks';
      return this.layoutMode;
    },
    compClassName() {
      let ans = `withLayoutMode:${this.actualLayoutMode}`;
      if (this.hasCustomHeaderOrFooter) ans += ' hasCustomHeaderOrFooter';
      if (this.dragging) ans += ' isDragging';
      return ans;
    },
    computedStyle() {
      const ans = [];
      if (this.gap) ans.push(`gap: ${this.gap}`);
      switch (this.actualLayoutMode) {
        case 'fixedColumnCount':
          ans.push(`grid-template-columns: repeat(${+this.columnCount || 1}, 1fr)`);
          break;
        case 'fixedColumnWidth':
          {
            const columnWidth = this.columnWidth || '300px';
            const autoColumnCountStrategy = this.autoColumnCountStrategy || 'grow';
            if (autoColumnCountStrategy === 'grow') {
              ans.push(`grid-template-columns: repeat( auto-fill, minmax( ${columnWidth}, 1fr) )`);
            } else if (autoColumnCountStrategy === 'auto-fit') {
              ans.push(`grid-template-columns: repeat( auto-fit, minmax( ${columnWidth}, 1fr) )`);
            } else {
              ans.push(`grid-template-columns: repeat( auto-fill, ${columnWidth} )`, `justify-content: ${autoColumnCountStrategy}`);
            }
            break;
          }
      }
      return ans.join('; ');
    },
    getCachedItemVNodeHandler() {
      if (!this.hiddenItemWhenEmpty) return () => null;
      return memoize(index => this.getOnRenderChild(index));
    },
    safeSelectedIndexes() {
      if (!this.selectable) return [];
      const originalValue = this.selectedIndex;
      const payload = (Array.isArray(originalValue) ? originalValue.slice() : [+originalValue]).filter(x => Number.isInteger(x) && x >= 0);
      return payload;
    }
  },
  methods: {
    /** 返回一个函数 用来在对应index的插槽渲染时 获得CRenderer内部的vnode 从而可知作用域插槽是否为空 */
    getOnRenderChild(index) {
      return vNode => {
        const hidden = !vNode;
        if (hidden !== !!this.isItemHidden[index]) {
          this.$set(this.isItemHidden, index, hidden);
        }
      };
    },
    async handleDragEnd({
      oldIndex,
      newIndex
    }) {
      const item = this.array[oldIndex];
      if (this.dragAction === 'mutateArray') {
        const newArray = this.array.slice();
        newArray.splice(oldIndex, 1);
        newArray.splice(newIndex, 0, item);
        this.array = newArray;
      }
      try {
        if (typeof this.onDragEnd === 'function') {
          await this.onDragEnd({
            oldIndex,
            newIndex,
            item
          });
        }
        let changedSelectIndexes = false;
        const payload = this.safeSelectedIndexes.map(index => {
          if (oldIndex === index) {
            changedSelectIndexes = true;
            return newIndex;
          }
          let delta = 0;
          if (oldIndex < index) delta -= 1;
          if (newIndex <= index) delta += 1;
          if (delta) changedSelectIndexes = true;
          return index + delta;
        });
        if (changedSelectIndexes) this.commitNewSelectedIndexes(payload);
      } catch (err) {
        console.error(`[卡片列表] 序号无法调整 ${oldIndex} -> ${newIndex}`, err);
      } finally {
        this.dragging = false;
      }
    },
    async handleItemClick(index) {
      if (!this.selectable) return;
      let payload = this.safeSelectedIndexes.slice();
      if (payload.includes(index)) {
        payload = payload.filter(i => i !== index);
      } else {
        payload = this.isSingleSelect ? [index] : Array.from(new Set([...payload, index]));
      }
      try {
        if (typeof this.onSelect === 'function') {
          await this.onSelect(index);
        }
        this.commitNewSelectedIndexes(payload);
      } catch (err) {
        console.error(`[卡片列表] 无法选中 ${index}`, err);
      }
    },
    commitNewSelectedIndexes(payload) {
      var _payload$;
      if (!this.selectable) return;

      // 根据配置情况 + 绑定值，推导应该 emit 什么类型的数据出去
      const useNumberAsOutput = this.isSingleSelect && !Array.isArray(this.selectedIndex);
      this.$emit('changeSelect', useNumberAsOutput ? (_payload$ = payload[0]) !== null && _payload$ !== void 0 ? _payload$ : -1 : payload);
    },
    checkSelected(index) {
      return this.selectable && this.safeSelectedIndexes.indexOf(index) > -1;
    }
  },
  ucEvents(decls) {
    if (this.dragOrder !== 'none') {
      decls.push({
        id: ':onDragEnd',
        name: '拖拽顺序后',
        desc: '可以通过 $event 获取到拖拽前、后序号',
        arguments: [{
          type: 'object',
          fields: [{
            id: 'oldIndex',
            type: 'number',
            title: '旧序号',
            description: '从0开始'
          }, {
            id: 'newIndex',
            type: 'number',
            title: '新序号',
            description: '从0开始'
          }, {
            id: 'item',
            type: 'any',
            title: '被拖拽的元素'
          }]
        }]
      });
    }
    if (this.deleteButton && this.deleteButton !== 'none') {
      decls.push({
        id: 'onDelete',
        name: '删除时',
        desc: '"终止事件流程"可以阻止默认的删除行为',
        arguments: [{
          type: 'object',
          description: '{index,value,array}',
          fields: [{
            type: 'number',
            id: 'index',
            description: '要删除的数据索引'
          }, {
            type: 'any',
            id: 'value',
            description: '当前即将删除的数据项'
          }, {
            type: 'array',
            id: 'array',
            description: '输入卡片列表的数组'
          }]
        }],
        asProp: 'onDelete'
      });
    }
    if (this.selectable) {
      decls.push({
        id: ':onSelect',
        name: '选中卡片',
        desc: '列表选中状态改变时',
        arguments: [{
          type: 'index',
          description: '当前选中（取消选中）的下标'
        }]
      });
    }
  }
};
var render = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('draggable', {
    staticClass: "w-array",
    class: _vm.compClassName,
    style: _vm.computedStyle,
    attrs: {
      "data-path": _vm.path,
      "value": _vm.array,
      "handle": _vm.dragOrder !== 'whole' ? '.w-array-drag' : null,
      "draggable": ".w-array-item",
      "disabled": _vm.isDesignMode || !_vm.canDragItem || !_vm.arrayLength,
      "filter": 'textarea, input',
      "prevent-on-filter": false,
      "animation": 300,
      "delay-on-touch-only": true
    },
    on: {
      "start": function ($event) {
        _vm.dragging = true;
      },
      "end": _vm.handleDragEnd
    }
  }, [_vm._l(_vm.itemRendererData, function (ref) {
    var slotProps = ref.slotProps;
    var index = ref.index;
    var key = ref.key;
    return _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: !_vm.isItemHidden[index],
        expression: "!isItemHidden[index]"
      }],
      key: key,
      ref: "arrayItemContainers",
      refInFor: true,
      class: {
        'w-array-item': true,
        selected: _vm.checkSelected(index),
        selectable: _vm.selectable
      },
      attrs: {
        "data-index": index
      },
      on: {
        "click": function ($event) {
          !_vm.isDesignMode && _vm.handleItemClick(index);
        }
      }
    }, [_vm.canDeleteItem ? _c('div', {
      staticClass: "w-array-delete",
      class: "w-array-delete--" + _vm.deleteButton
    }, [_c('i', {
      staticClass: "fa fa-trash",
      on: {
        "click": function ($event) {
          return _vm.deleteItem(index);
        }
      }
    })]) : _vm._e(), _vm._v(" "), _vm.canDragItem && _vm.dragOrder !== 'whole' ? _c('div', {
      staticClass: "w-array-drag",
      class: "w-array-drag--" + _vm.dragOrder
    }, [_c('i', {
      staticClass: "fa fa-bars"
    })]) : _vm._e(), _vm._v(" "), _vm._t("item", null, {
      "onGeneratorVNode": _vm.getCachedItemVNodeHandler(index)
    }, slotProps)], 2);
  }), _vm._v(" "), _vm.enableHeaderSlot && (_vm.isDesignMode || _vm.$slots.header) ? _c('div', {
    key: "header",
    ref: "headerSlot",
    staticClass: "w-array-item w-array-customHeader uc-slotForBlocks",
    attrs: {
      "slot": "header"
    },
    slot: "header"
  }, [_vm._t("header", function () {
    return [_vm.isDesignMode ? _c('div', {
      staticClass: "w-array-customSlotEmptyPlaceholder"
    }, [_vm._v("\n        在这里放置元素\n      ")]) : _vm._e()];
  })], 2) : _vm._e(), _vm._v(" "), _vm.enableFooterSlot && (_vm.isDesignMode || _vm.$slots.footer) ? _c('div', {
    key: "footer",
    ref: "footerSlot",
    staticClass: "w-array-item w-array-customFooter uc-slotForBlocks",
    attrs: {
      "slot": "footer"
    },
    slot: "footer"
  }, [_vm._t("footer", function () {
    return [_vm.isDesignMode ? _c('div', {
      staticClass: "w-array-customSlotEmptyPlaceholder"
    }, [_vm._v("\n        在这里放置元素\n      ")]) : _vm._e()];
  })], 2) : _vm._e(), _vm._v(" "), _vm.emptySlotShown && !_vm.hasCustomHeaderOrFooter ? _c('div', {
    ref: "emptySlot",
    staticClass: "w-array-emptySlot uc-slotForBlocks",
    attrs: {
      "slot": "footer"
    },
    slot: "footer"
  }, [_vm._t("empty", function () {
    return [_c('div', {
      staticClass: "w-array-emptyDefault"
    }, [_c('p', [_vm._v("没有内容")]), _vm._v(" "), _c('p', [!_vm.finalReadonly && _vm.addButton !== 'hidden' ? _c('button', {
      staticClass: "uc-button w-array-add",
      attrs: {
        "type": "button"
      },
      on: {
        "click": function ($event) {
          return _vm.addItem();
        }
      }
    }, [_vm._v("\n            " + _vm._s(_vm.addItemButtonText || '添加') + "\n          ")]) : _vm._e()])])];
  })], 2) : !_vm.finalReadonly && _vm.addButton !== 'hidden' ? _c('div', {
    key: "sysFooter",
    staticClass: "w-array-footer",
    attrs: {
      "slot": "footer"
    },
    slot: "footer"
  }, [!_vm.finalReadonly && _vm.addButton !== 'hidden' ? [_vm.addButton === 'default' ? _c('button', {
    staticClass: "uc-button w-array-add",
    attrs: {
      "type": "button"
    },
    on: {
      "click": function ($event) {
        return _vm.addItem();
      }
    }
  }, [_vm._v("\n        " + _vm._s(_vm.addItemButtonText || '+ 添加一项') + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.addButton === 'plus-icon' ? _c('div', {
    staticClass: "w-array-add--plus-icon",
    attrs: {
      "tabindex": "0"
    },
    on: {
      "click": function ($event) {
        return _vm.addItem();
      },
      "keypress": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.addItem();
      }
    }
  }, [_c('div', {
    staticClass: "w-array-add--plus-icon-theIcon"
  }, [_vm._v("\n          ＋\n        ")]), _vm._v("\n        " + _vm._s(_vm.addItemButtonText || '添加') + "\n      ")]) : _vm._e()] : _vm._e()], 2) : _vm._e()], 2);
};
var staticRenderFns = [];
const __script__ = __script_base__;
export default Object.assign({
  render,
  staticRenderFns,
  _compiled: true
}, {
  "__file": "builtin/components/Array/index.vue"
}, typeof __script__ === 'function' ? __script__.options : __script__);